import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dataService } from '../../services/dataService';
import { InsulinScheduleView } from '../../types/domain';
import { BadgeStatus, GamificationSummary, computeGamification, localToday } from '../../lib/gamification';

/**
 * Scores the patient's whole log (FR-19).
 *
 * `schedule` is passed in rather than fetched: Home already has it, and the
 * complete-day bonus is judged against it. Pass null while it is still
 * loading so the score is not computed against an empty regimen first.
 */
export function useGamification(
  userId: string | undefined,
  schedule: InsulinScheduleView[] | null
): GamificationSummary | null {
  const [summary, setSummary] = useState<GamificationSummary | null>(null);

  useEffect(() => {
    if (!userId || schedule === null) {
      return;
    }
    let cancelled = false;
    dataService.getScoringLog(userId).then(log => {
      if (!cancelled) {
        setSummary(computeGamification(log, schedule, localToday()));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userId, schedule]);

  return summary;
}

export function BadgeTile({ badge }: { badge: BadgeStatus }) {
  return (
    <div
      className={`game-badge${badge.earned ? ' game-badge--earned' : ''}`}
      aria-label={
        badge.earned
          ? `${badge.title}, sudah didapat`
          : `${badge.title}, ${badge.progress} dari ${badge.days} hari`
      }
    >
      <span className="game-badge-emoji" aria-hidden="true">
        {badge.earned ? badge.emoji : '🔒'}
      </span>
      <span className="game-badge-title">{badge.title}</span>
      <span className="game-badge-note">
        {badge.earned ? 'Didapat!' : `${badge.progress}/${badge.days} hari`}
      </span>
    </div>
  );
}

/** Home's summary: streak, points, today, and the way to the full page. */
export function GamificationCard({ summary }: { summary: GamificationSummary | null }) {
  if (!summary) {
    return null;
  }

  const next = summary.nextBadge;

  return (
    <Link to="/prestasi" className="game-card" aria-label="Lihat poin dan lencana">
      <div className="game-card-stats">
        <div className="game-stat">
          <span className="game-stat-icon" aria-hidden="true">⭐</span>
          <span className="game-stat-value">{summary.totalPoints}</span>
          <span className="game-stat-label">Poin</span>
        </div>
        <div className="game-stat">
          <span className="game-stat-icon" aria-hidden="true">🔥</span>
          <span className="game-stat-value">{summary.currentStreak}</span>
          <span className="game-stat-label">Hari beruntun</span>
        </div>
        <div className="game-stat">
          <span className="game-stat-icon" aria-hidden="true">✨</span>
          <span className="game-stat-value">+{summary.today.total}</span>
          <span className="game-stat-label">Hari ini</span>
        </div>
      </div>
      <div className="game-card-footer">
        <span>
          {next
            ? `${next.days - next.progress} hari lagi menuju ${next.title}!`
            : 'Semua lencana sudah didapat. Hebat!'}
        </span>
        <span aria-hidden="true">→</span>
      </div>
    </Link>
  );
}
